import type { FC } from "react";
import { useEffect, useState } from "react";
import { Col, Container, ProgressBar, Row } from "react-bootstrap";
import Button from "react-bootstrap/Button";

import { BookmarkIcon, PauseExerciseModal } from "components";

import { useAppDispatch, useAppSelector } from "store/hooks";
import {
  getGlobalTime,
  isTimerShown,
  startAssignmentTimer,
  startGlobalTimer,
  stopAllTimers,
  tick,
} from "store/slices/timer";

import { BlacklistButton } from "features/Blacklist";
import { ClassroomAmbienceButton, ClassroomAmbienceProvider } from "features/ClassroomAmbience";
import PauseIcon from "images/icons/pause.svg";

interface Props {
  isProgressBarVisible: boolean;
  totalTime: number;
  isTimerStopped: boolean;
  userAssignmentId: string | undefined;
  onQuit: () => void;
}

const ExerciseBar: FC<Props> = ({ isProgressBarVisible, totalTime, isTimerStopped, userAssignmentId, onQuit }) => {
  const [isPauseModalVisible, setPauseModalVisible] = useState(false);
  const dispatch = useAppDispatch();
  const time = useAppSelector(getGlobalTime);
  const isTimerVisible = useAppSelector(isTimerShown);

  const getTimeFromTimestamp = (timestamp: number) => {
    const remainingTime = Math.abs(totalTime - timestamp);

    const date = new Date(remainingTime * 1000);
    const hours = date.getUTCHours();
    const minutes = date.getMinutes();
    const seconds = date.getSeconds();

    return `${hours * 60 + minutes}:${seconds < 10 ? `0${seconds}` : seconds}`;
  };

  const handlePauseClick = () => {
    setPauseModalVisible(true);
    dispatch(stopAllTimers());
  };

  const handleModalClose = () => {
    setPauseModalVisible(false);
    dispatch(startGlobalTimer());
    dispatch(startAssignmentTimer());
  };

  useEffect(() => {
    if (isTimerStopped) return;

    const interval = setInterval(() => {
      dispatch(tick());
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  return (
    <ClassroomAmbienceProvider>
      {isProgressBarVisible ? <ProgressBar now={Math.max(0, (1 - time / totalTime) * 100)} /> : null}

      <Container className="lh-1 user-select-none px-3">
        <Row className="py-20px">
          <Col className="d-flex align-items-center">
            {isTimerVisible ? (
              <strong id="timer" className="timer">
                {getTimeFromTimestamp(time)}
              </strong>
            ) : null}
          </Col>
          <Col className="text-end">
            <ClassroomAmbienceButton id="sound" />

            {typeof userAssignmentId === "string" && userAssignmentId.length > 0 ? (
              <>
                <BlacklistButton id="blackEnvelope" className="ms-20px" userAssignmentId={userAssignmentId} />
                <BookmarkIcon className="ms-20px" userAssignmentId={userAssignmentId} />
              </>
            ) : null}

            <Button size={"sm"} variant={"navigation"} onClick={handlePauseClick} className="ms-20px p-0 ">
              <PauseIcon />
            </Button>
          </Col>
        </Row>

        {isPauseModalVisible ? <PauseExerciseModal onClose={handleModalClose} onQuit={onQuit} /> : null}
      </Container>
    </ClassroomAmbienceProvider>
  );
};

export default ExerciseBar;
